import { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { PLATFORM_FEE_PERCENT, type Tournament } from '../types';
import { callJoinTournament, useMySquads } from '../services/tournamentService';
import { useWalletData } from '../services/walletService';
import { useAuth } from '../context/AuthContext';
import { Clock, Search, Trophy, Copy, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { collection, onSnapshot, query, orderBy, where, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useModal } from '../context/ModalContext';
import { useWalletLock } from '../context/WalletLockContext';
import TCoinIcon from '../components/TCoinIcon';

const games = ['All', 'PUBG', 'Free Fire', 'Wild Rift', 'MLBB'];
const modes = ['All', 'Solo', 'Squad', 'Team'];
const statuses = ['All', 'registering', 'registration closed', 'live', 'completed'];

function getGameClass(game: string) {
    if (game === 'PUBG') return 'pubg';
    if (game === 'Free Fire') return 'freefire';
    if (game === 'MLBB') return 'mlbb';
    return 'wildrift';
}

function getTimeLeft(start: string, status: string) {
    const diff = new Date(start).getTime() - Date.now();
    if (status === 'live') return 'In Progress';
    if (status === 'completed') return 'Ended';
    if (diff <= 0) return 'Starting soon';
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (d > 0) return `${d}d ${h}h`;
    return `${h}h ${m}m`;
}

export default function Tournaments() {
    const { showAlert } = useModal();
    const { requireUnlock } = useWalletLock();
    const { user } = useAuth();
    const { wallet } = useWalletData(user?.uid);
    const walletBalance = wallet.balance;
    const [gameFilter, setGameFilter] = useState('All');
    const [modeFilter, setModeFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [search, setSearch] = useState('');

    const [copiedField, setCopiedField] = useState<string | null>(null);
    const handleCopy = (e: React.MouseEvent, text: string) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopiedField(text);
        setTimeout(() => setCopiedField(null), 2000);
    };

    // --- REAL DATA INTEGRATION ---
    const [realTournaments, setRealTournaments] = useState<Tournament[]>([]);
    const [joinedIds, setJoinedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        const q = query(
            collection(db, 'tournaments'), 
            where('status', 'in', ['open', 'registering', 'registration closed', 'upcoming', 'live']),
            orderBy('startTime', 'asc'),
            limit(100)
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data: Tournament[] = [];
            snapshot.forEach((d) => {
                data.push({ id: d.id, ...d.data() } as Tournament);
            });
            setRealTournaments(data);
        });
        return () => unsubscribe();
    }, []);

    // Track which tournaments the user has joined (real-time)
    useEffect(() => {
        if (!user?.uid) return;
        const entriesQuery = query(
            collection(db, 'entries'),
            where('userId', '==', user.uid),
            orderBy('joinedAt', 'desc')
        );
        
        const unsubscribe = onSnapshot(entriesQuery, (snapshot) => {
            const ids = new Set<string>();
            snapshot.forEach((d) => {
                const data = d.data();
                if (data.status !== 'refunded') {
                    ids.add(data.tournamentId);
                }
            });
            setJoinedIds(ids);
        });
        return () => unsubscribe();
    }, [user?.uid]);

    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const gameQuery = queryParams.get('game');
        if (gameQuery) {
            if (gameQuery.includes('PUBG')) setGameFilter('PUBG');
            else if (gameQuery.includes('Free Fire')) setGameFilter('Free Fire');
            else if (gameQuery.includes('Wild Rift')) setGameFilter('Wild Rift');
            else if (gameQuery.includes('Mobile Legends') || gameQuery.includes('MLBB')) setGameFilter('MLBB');
        } else {
            setGameFilter('All');
        }
    }, [location.search]);

    const [selectedTournament, setSelectedTournament] = useState<any>(null);
    const [joinState, setJoinState] = useState<'confirm' | 'success'>('confirm');
    const [joining, setJoining] = useState(false);



    const { squads: mySquads } = useMySquads(user?.uid);

    const handleJoinClick = (e: React.MouseEvent, t: any) => {
        e.preventDefault();
        e.stopPropagation();

        if (t.mode !== 'Solo') {
            const playerSquad = mySquads.find(s => s.game === t.game);
            if (!playerSquad) {
                showAlert(`You must be in a ${t.game} squad to join a ${t.mode} tournament.\nPlease create a squad in your Profile first.`, 'Squad Required');
                return;
            }
            if (playerSquad.status === 'forming') {
                showAlert("Your squad is not fully formed yet! All members must accept their invitations before you can register for a tournament.", 'Squad Forming');
                return;
            }
            const uid = user?.uid || '';
            const isLeader = playerSquad.members.some(m => m.uid === uid && m.role === 'leader');
            const isCoLeader = playerSquad.members.some(m => m.uid === uid && m.role === 'co-leader');
            if (!isLeader && !isCoLeader) {
                showAlert(`Only the Squad Leader or Co-Leader can join tournaments on behalf of the squad.`, 'Permission Denied');
                return;
            }
        }

        // ── Wallet Lock Gate ──
        requireUnlock(() => {
            setSelectedTournament(t);
            setJoinState('confirm');
        });
    };

    const handleConfirmJoin = async () => {
        const t = selectedTournament;
        if (!t) return;

        if (walletBalance < t.entryFee) {
            showAlert('Insufficient balance to join tournament!', 'Error');
            setSelectedTournament(null);
            return;
        }

        // Live mode: call Cloud Function
        setJoining(true);
        try {
            const squadId = t.mode !== 'Solo' ? mySquads.find((s: any) => s.game === t.game)?.id : undefined;
            const result = await callJoinTournament(t.id, squadId);
            if (result.success) {
                setJoinState('success');
            } else {
                showAlert(result.message || 'Failed to join tournament.', 'Error');
                setSelectedTournament(null);
            }
        } catch (err: unknown) {
            showAlert(err instanceof Error ? err.message : 'Failed to join tournament. Please try again.', 'Error');
            setSelectedTournament(null);
        } finally {
            setJoining(false);
        }
    };

    const filtered = realTournaments.filter(t => {
        if (gameFilter !== 'All' && t.game !== gameFilter) return false;
        if (modeFilter !== 'All' && t.mode !== modeFilter) return false;
        if (statusFilter !== 'All' && t.status !== statusFilter) return false;
        if (search) {
            const searchLower = search.toLowerCase();
            const matchesTitle = t.title.toLowerCase().includes(searchLower);
            const matchesId = t.id.toLowerCase().includes(searchLower);
            if (!matchesTitle && !matchesId) return false;
        }
        return true;
    });

    return (
        <div className="page-wrapper">
            <h1 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.8rem', marginBottom: 4 }} className="animate-in">
                🎮 All Tournaments
            </h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24 }} className="animate-in">Browse and join skill-based tournaments</p>

            {/* Search */}
            <div className="form-group animate-in" style={{ position: 'relative', maxWidth: 420, marginBottom: 16 }}>
                <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input className="form-input" placeholder="Search tournaments..." value={search}
                    onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 40 }}
                />
            </div>

            {/* Filters */}
            <div className="animate-in" style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, alignSelf: 'center', minWidth: 40 }}>Game:</span>
                    <div className="filter-bar" style={{ marginBottom: 0 }}>
                        {games.map(g => (
                            <button key={g} className={`filter-chip ${gameFilter === g ? 'active' : ''}`} onClick={() => setGameFilter(g)}>{g}</button>
                        ))}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, alignSelf: 'center', minWidth: 40 }}>Mode:</span>
                    <div className="filter-bar" style={{ marginBottom: 0 }}>
                        {modes.map(m => (
                            <button key={m} className={`filter-chip ${modeFilter === m ? 'active' : ''}`} onClick={() => setModeFilter(m)}>{m}</button>
                        ))}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, alignSelf: 'center', minWidth: 40 }}>Status:</span>
                    <div className="filter-bar" style={{ marginBottom: 0 }}>
                        {statuses.map(s => (
                            <button key={s} className={`filter-chip ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
                                {s === 'All' ? 'All' : s === 'registering' ? 'Registering' : s === 'registration closed' ? 'Registration Closed' : s.charAt(0).toUpperCase() + s.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Results */}
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 16 }}>{filtered.length} tournament{filtered.length !== 1 ? 's' : ''} found</p>

            <div className="tournament-grid">
                {filtered.map((t, i) => {
                    const fillPct = Math.round((t.slotFill / t.maxSlots) * 100);
                    const currentPool = Math.round(t.slotFill * t.entryFee * (1 - PLATFORM_FEE_PERCENT));
                    const estPool = Math.round(t.maxSlots * t.entryFee * (1 - PLATFORM_FEE_PERCENT));
                    const hasJoined = joinedIds.has(t.id);
                    return (
                        <NavLink to={`/tournaments/${t.id}`} key={t.id}>
                            <div className={`tournament-card animate-in stagger-${(i % 6) + 1}`}>
                                <div className="tc-header">
                                    <span className={`tc-game-badge ${getGameClass(t.game)}`}>{t.game} • {t.mode}</span>
                                    <span className={`tc-status ${t.status}`}>
                                        {t.status === 'live' && <span className="pulse-dot" />}
                                        {t.status}
                                    </span>
                                </div>
                                <div className="tc-title">{t.title}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, marginTop: -4 }} onClick={e => e.preventDefault()}>
                                    <span style={{ fontSize: '0.68rem', fontFamily: 'monospace', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: 4, color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                                        {t.id}
                                    </span>
                                    <button onClick={(e) => handleCopy(e, t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedField === t.id ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 2, transition: 'color 0.2s' }} title="Copy ID">
                                        {copiedField === t.id ? <Check size={12} /> : <Copy size={12} />}
                                    </button>
                                </div>
                                {/* Registration Countdown Timer */}
                                {t.registrationCloseTime && (t.status === 'registering' || t.status === 'open') && (
                                    <RegistrationCountdown closeTime={t.registrationCloseTime} />
                                )}
                                <div className="tc-mode">Starts {new Date(t.startTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                                <div className="tc-info-row">
                                    <div className="tc-info-item"><div className="tc-info-label">Current Pool</div><div className="tc-info-value prize"><TCoinIcon size={14} /> {currentPool.toLocaleString()}</div></div>
                                    <div className="tc-info-item"><div className="tc-info-label">Est. Pool</div><div className="tc-info-value" style={{ color: 'var(--text-muted)' }}><TCoinIcon size={14} /> {estPool.toLocaleString()}</div></div>
                                    <div className="tc-info-item"><div className="tc-info-label">Entry Fee</div><div className="tc-info-value fee"><TCoinIcon size={14} /> {t.entryFee}</div></div>
                                </div>
                                <div className="tc-slots">
                                    <div className="tc-slots-bar">
                                        <motion.div className="tc-slots-fill" 
                                            initial={{ width: 0 }}
                                            animate={{ width: `${fillPct}%` }}
                                            transition={{ duration: 1, ease: "easeOut" }}
                                            style={{ height: '100%', borderRadius: 5, background: fillPct >= 75 ? 'var(--success)' : fillPct >= 50 ? 'var(--warning)' : 'var(--accent)' }} 
                                        />
                                    </div>
                                    <div className="tc-slots-text"><span>{t.slotFill}/{t.maxSlots}</span><span>{fillPct}%</span></div>
                                </div>
                                <div className="tc-footer">
                                    <div className="tc-countdown"><Clock size={14} />{getTimeLeft(t.startTime, t.status)}</div>
                                    {(t.status === 'registering' || t.status === 'open') && !hasJoined && <button className="btn btn-primary btn-sm" onClick={e => handleJoinClick(e, t)}>Join</button>}
                                    {(t.status === 'registering' || t.status === 'open' || t.status === 'registration closed' || t.status === 'upcoming') && hasJoined && <span className="badge approved" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>Joined</span>}
                                    {(t.status === 'registration closed' || t.status === 'upcoming') && !hasJoined && <span className="badge" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--error)' }}>Registrations Closed</span>}
                                    {t.status === 'live' && <span className="badge approved">In Progress</span>}
                                    {t.status === 'completed' && <span className="badge" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>Processing Results</span>}
                                </div>
                            </div>
                        </NavLink>
                    );
                })}
            </div>

            {filtered.length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
                    <p style={{ fontSize: '2rem', marginBottom: 8 }}>🔍</p>
                    <p>No tournaments match your filters.</p>
                </div>
            )}

            {/* Join Modal */}
            {selectedTournament && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1000, padding: 20
                }}>
                    <div className="card animate-in" style={{ maxWidth: 400, width: '100%', textAlign: 'center', padding: 32 }}>
                        {joinState === 'confirm' ? (
                            <>
                                <h3 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 16 }}>Confirm Registration</h3>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
                                    Are you sure you want to join <strong>{selectedTournament.title}</strong>? 
                                    This action will reserve your slot for the tournament.
                                </p>
                                
                                <div style={{ background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16, marginBottom: 24 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.9rem' }}>
                                        <span style={{ color: 'var(--text-muted)' }}>Current Balance</span>
                                        <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}><TCoinIcon size={14} /> {walletBalance.toLocaleString()}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: '0.9rem' }}>
                                        <span style={{ color: 'var(--text-muted)' }}>Joining Fee</span>
                                        <span style={{ fontWeight: 600, color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '4px' }}>-<TCoinIcon size={14} /> {selectedTournament.entryFee}</span>
                                    </div>
                                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ fontWeight: 600 }}>Remaining Balance</span>
                                        <span style={{ fontWeight: 700, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '4px' }}><TCoinIcon size={14} /> {(walletBalance - selectedTournament.entryFee).toLocaleString()}</span>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 12 }}>
                                    <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setSelectedTournament(null)}>No, Cancel</button>
                                    <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleConfirmJoin} disabled={joining}>{joining ? 'Joining...' : 'Yes, Join Now'}</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                    <Trophy size={32} />
                                </div>
                                <h3 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 12 }}>
                                    Joined {selectedTournament.title}!
                                </h3>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
                                    Good luck, <strong>{user?.displayName || 'Player'}</strong>! Your entry fee has been securely locked until the tournament ends.
                                </p>
                                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setSelectedTournament(null)}>Back to Tournament</button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Registration Countdown Component ───────────────────────────────────────

function RegistrationCountdown({ closeTime }: { closeTime: string }) {
    const calcRemaining = useCallback(() => {
        return Math.max(0, new Date(closeTime).getTime() - Date.now());
    }, [closeTime]);

    const [remaining, setRemaining] = useState(calcRemaining);

    useEffect(() => {
        const timer = setInterval(() => {
            const r = calcRemaining();
            setRemaining(r);
            if (r <= 0) clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
    }, [calcRemaining]);

    if (remaining <= 0) {
        return (
            <div style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, margin: '4px 0' }}>
                🔒 Registration Closed
            </div>
        );
    }

    const hours = Math.floor(remaining / 3600000);
    const mins = Math.floor((remaining % 3600000) / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    const pad = (n: number) => n.toString().padStart(2, '0');

    return (
        <div style={{
            fontSize: '0.73rem', color: 'var(--accent-hover)', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 5, margin: '4px 0',
            padding: '3px 8px', borderRadius: 6,
            background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.12)',
            width: 'fit-content',
        }}>
            <span style={{ opacity: 0.7 }}>⏱</span> Registration closes in
            <span style={{ fontFamily: 'monospace', fontWeight: 800, letterSpacing: 0.5 }}>
                {pad(hours)}:{pad(mins)}:{pad(secs)}
            </span>
        </div>
    );
}
